import React, { useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Summary } from "../../widgets/Summary/Summary";
import { LabeledBox } from "../../shared/LabeledBox/LabeledBox";
import { useAppTranslation } from "../../../../../i18n/useAppTranslation";
import { SelectPayment } from "../../shared/SelectPayment/SelectPayment";
import { dispatch } from "../../../../../store/store";
import { checkoutInvoice } from "../../../../../store/checkoutInvoice";
import { PAYMENT_VIEW_TYPES } from "../../../../../store/checkoutInvoice/consts";
import { StageGrid } from "../../shared/StageGrid/StageGrid";
import { Amount } from "../../widgets/Amount/Amount";
import { useInvoice } from "../../hooks/invoice/useInvoice";
import { http as httpService } from "../../../../../services/HttpService";
import classes from "../../SalesCheckoutInvoice.module.scss";
import { withGlobalSettings } from "../../../../../hocs/withGlobalSettings";
import { useCurrentUserQuery } from "../../../../../api/queries/useUserQuery";

export function StageStartBase({ globalSettings }) {
  const { tCommon } = useAppTranslation.Common();
  const { invoice } = useInvoice();
  const { data: currentUser } = useCurrentUserQuery(); // fetch current user
  const queryClient = useQueryClient();

  const onPaymentOptionsOpen = () => {
    queryClient.invalidateQueries("availablePaymentMethods");
    if (invoice.totalAmount === 0) {
      dispatch(
        checkoutInvoice.actions.paymentViewChange({
          paymentView: PAYMENT_VIEW_TYPES.cash,
        }),
      );
    } else {
      dispatch(
        checkoutInvoice.actions.paymentViewChange({
          paymentView: PAYMENT_VIEW_TYPES.optionSelection,
        }),
      );
    }
  };

  // Sunbit Payment Path integration (moved from SalesCheckoutInvoice)
  const [sunbitToken, setSunbitToken] = useState(null);
  const [isSunbitLoading, setIsSunbitLoading] = useState(false);
  const [sunbitError, setSunbitError] = useState(null);
  const sunbitInitializedRef = useRef(false);

  const SUNBIT_KEY = process.env.REACT_APP_SUNBIT_KEY;
  const SUNBIT_MODE = process.env.REACT_APP_SUNBIT_MODE || "SANDBOX";
  const hasSunbitSdkFeature = Boolean(globalSettings?.feature_flag?.sunbit_sdk); // feature flag check

  const patientId = invoice?.patient?.id; // extract patient ID
  const accountId = currentUser?.account?.id; // extract account ID

  // new state for response details
  const [sunbitCustomerDetails, setSunbitCustomerDetails] = useState(null);
  const [sunbitRepresentativeDetails, setSunbitRepresentativeDetails] =
    useState(null);
  const [, setSunbitTokenExpiresAt] = useState(null); // keep expiresAt for potential future refresh

  const fetchSunbitToken = async () => {
    if (!SUNBIT_KEY || !patientId) return null; // skip if not configured or patient missing
    setIsSunbitLoading(true);
    setSunbitError(null);
    try {
      const { data: response } = await httpService.get(
        `sunbit/merchant-authentication/${patientId}`,
      );
      const payload = (response && response.data) || {};
      const auth = payload.authentication || {};
      const token = auth.token || null;
      if (!token) {
        setSunbitError("Invalid Sunbit token response");
        return null;
      }
      // ancillary data first
      setSunbitTokenExpiresAt(auth.expiresAt || null);
      setSunbitCustomerDetails(payload.customerDetails || {});
      setSunbitRepresentativeDetails(payload.representativeDetails || {});
      setSunbitToken(token);
      return token;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Sunbit token fetch failed", e);
      setSunbitError("Failed to fetch Sunbit token");
      return null;
    } finally {
      setIsSunbitLoading(false);
    }
  };

  const loadSunbitScript = () => {
    if (!SUNBIT_KEY) return Promise.resolve();
    if (document.getElementById("sunbit-sdk-script")) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.id = "sunbit-sdk-script";
      script.src = "https://static.sunbit.com/sdk/sunbit-sdk.js";
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const initSunbit = () => {
    if (!SUNBIT_KEY || sunbitInitializedRef.current) return;
    if (!window.SUNBIT || !sunbitToken) return;
    if (!sunbitCustomerDetails || !sunbitRepresentativeDetails) return;
    if (!accountId || !patientId) return;
    const formattedReferenceId = `ar-${accountId}-${patientId}`;
    try {
      window.SUNBIT.init({
        sunbitKey: SUNBIT_KEY,
        enablePaymentPath: true,
        mode: SUNBIT_MODE,
      });
      window.SUNBIT.PaymentPathModule.then((paymentPath) => {
        paymentPath.init("#payment-path-container", {
          token: sunbitToken,
          referenceId: formattedReferenceId,
          totalAmount: Number(invoice?.totalAmount || 0),
          customerDetails: sunbitCustomerDetails || {},
          representativeDetails: sunbitRepresentativeDetails || {},
          // only keep token expiration callback; others removed for lint cleanliness
          onTokenExpired: function () {
            fetchSunbitToken().then((newToken) => {
              if (
                newToken &&
                window.SUNBIT &&
                window.SUNBIT.PaymentPathModule
              ) {
                window.SUNBIT.PaymentPathModule.then((pp) =>
                  pp.setToken(newToken),
                );
              }
            });
          },
        });
        sunbitInitializedRef.current = true;
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Sunbit init error", e);
      setSunbitError("Sunbit initialization failed");
    }
  };

  useEffect(() => {
    if (!hasSunbitSdkFeature) return; // skip if feature flag off
    if (!patientId) return; // wait for patient
    let canceled = false;
    const setup = async () => {
      if (!SUNBIT_KEY) return;
      try {
        await loadSunbitScript();
        if (canceled) return;
        await fetchSunbitToken();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Sunbit script load failed", e);
        setSunbitError("Sunbit script failed to load");
      }
    };
    setup();
    return () => {
      canceled = true;
    };
  }, [hasSunbitSdkFeature, patientId]);

  useEffect(() => {
    if (
      hasSunbitSdkFeature &&
      sunbitToken &&
      window.SUNBIT &&
      sunbitCustomerDetails &&
      sunbitRepresentativeDetails &&
      accountId &&
      patientId
    ) {
      initSunbit();
    }
  }, [
    hasSunbitSdkFeature,
    sunbitToken,
    sunbitCustomerDetails,
    sunbitRepresentativeDetails,
    accountId,
    patientId,
  ]);
  useEffect(() => {
    window.__refreshSunbitToken = fetchSunbitToken;
  }, []); // debug helper

  return (
    <StageGrid>
      <Amount />
      <Summary />
      <LabeledBox label={tCommon("label.payment")}>
        {hasSunbitSdkFeature && SUNBIT_KEY && (
          <div
            className={classes.sunbitPaymentPathWrapper}
            style={{ marginTop: 12 }}
          >
            <button
              id="payment-path-container"
              type="button"
              disabled={isSunbitLoading || !!sunbitError}
              className={classes.sunbitPaymentPathButton}
            >
              {isSunbitLoading ? "Loading Sunbit..." : "Explore Payment Plans"}
            </button>
            {sunbitError && (
              <div
                className={classes.sunbitError}
                style={{ color: "#c00", marginTop: 4 }}
              >
                {sunbitError}
              </div>
            )}
          </div>
        )}
        <SelectPayment onSelect={onPaymentOptionsOpen} />
      </LabeledBox>
    </StageGrid>
  );
}

export const StageStart = withGlobalSettings(StageStartBase); // wrap with HOC
